/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PasswordRules from '../common/PasswordRules';
import { validatePassword } from '../../utils/passwordPolicy';

// PRICE-005: units-based tiers, matching PLANS in server.js exactly.
// This array has no shared source with server.js/Pricing.js/UpgradePrompts.js
// — it drifted silently once before (the old comment here claimed it was
// "kept in sync," which nothing actually enforced). If prices or
// allowances change again, this file needs updating by hand along with
// the other three.
const REG_PLANS = [
  { key: 'free',     name: 'Free',     color: '#6b7280', monthlyPrice: 0,     annualPrice: 0,      units: '4 let units',   tagline: '1 user · core features' },
  { key: 'solo',     name: 'Solo',     color: '#0891b2', monthlyPrice: 8.00,  annualPrice: 80.00,  units: '4 let units',   tagline: '1 user · reports, AI scanning & appraisal' },
  { key: 'standard', name: 'Standard', color: '#2563eb', monthlyPrice: 17.50, annualPrice: 175.00, units: '24 let units',  tagline: '3 users · reports, AI scanning & appraisal' },
  { key: 'pro',      name: 'Pro',      color: '#7c3aed', monthlyPrice: 35.00, annualPrice: 350.00, units: '60 let units',  tagline: '10 users · refurbishment projects & service requests', popular: true },
  { key: 'agency',   name: 'Agency',   color: '#065f46', monthlyPrice: null,  annualPrice: null,   units: '150 let units', tagline: 'Custom pricing — talk to us' },
];

// BUG FIX: ?signup=1 was sent by the marketing site expecting it to
// pre-select registration (this was even documented as a planned fix,
// WEB-006, in the marketing site's own DEPLOY.md) but was never actually
// read anywhere in this file — view always defaulted to 'login' regardless.
// Also reads a new ?plan= parameter so a link can route straight into a
// SPECIFIC paid plan's registration (e.g. ?signup=1&plan=standard&interval=year),
// rather than everyone landing on the registration form with Free
// pre-selected by default and having to notice and change it themselves.
const VALID_PLANS = ['free', 'solo', 'standard', 'pro', 'agency'];
const getInitialViewAndPlan = () => {
  const params = new URLSearchParams(window.location.search);
  const planParam = params.get('plan');
  const plan = VALID_PLANS.includes(planParam) ? planParam : 'free';
  const view = (params.get('signup') === '1' || planParam) ? 'register' : 'login';
  const interval = params.get('interval') === 'year' ? 'year' : 'month';
  return { view, plan, interval };
};

export default function Login() {
  const initial = getInitialViewAndPlan();
  const [view, setView] = useState(initial.view); // login | register | mfa
  const [mfaPendingToken, setMfaPendingToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Login form
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Register form
  const [regOrg, setRegOrg] = useState('');
  const [regEmail, setRegEmail] = useState('');

  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [acceptLegal, setAcceptLegal] = useState(false);
  const [regPlan, setRegPlan] = useState(initial.plan);
  const [regInterval, setRegInterval] = useState(initial.interval);
  const [checkoutError, setCheckoutError] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reason = params.get('reason');
    if (reason === 'inactivity') setError('You were logged out due to 60 minutes of inactivity.');
    if (reason === 'expired') setError('Your session has expired. Please log in again.');
  }, [location.search]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setOrgOptions([]);
    setLoading(true);
    try {
      const BASE = API_URL;
      const body = { username, password };
      if (selectedOrg) body.organization_name = selectedOrg;
      const res = await fetch(`${BASE}/api/login`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify(body)
      }).then(r => r.json());
      
      if (res.error === 'multiple_orgs') {
        setOrgOptions(res.organizations);
        setLoading(false);
        return;
      }

      // MFA required — show MFA prompt
      if (res.mfa_required) {
        setMfaPendingToken(res.mfa_pending_token);
        setView('mfa');
        setLoading(false);
        return;
      }

      if (res.error || !res.token) throw new Error(res.message || res.error || 'Login failed');
      
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      await login(username, password, selectedOrg || undefined);
      navigate('/');
    } catch (err) {
      setError(err.message || err.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaLoading(true);
    try {
      const BASE = API_URL;
      const res = await fetch(`${BASE}/api/auth/mfa/verify`, {
        method: 'POST', headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ mfa_pending_token: mfaPendingToken, code: mfaCode.replace(/\s/g, '') })
      }).then(r => r.json());
      if (res.error || !res.token) throw new Error(res.error || 'Invalid code');
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      await login(username, password, selectedOrg || undefined);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
    } finally {
      setMfaLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (regOrg.length < 2) return setError('Organization name must be at least 2 characters');
    if (!regEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(regEmail)) return setError('A valid email address is required — it\'s also what you\'ll use to log in.');
    if (!acceptLegal) return setError('You must accept the Terms of Service, EULA and Privacy Policy to create an account.');

    const passwordProblem = validatePassword(regPassword);
    if (passwordProblem) return setError(passwordProblem);
    if (regPassword !== regConfirm) return setError('Passwords do not match');

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: regEmail, password: regPassword, organization_name: regOrg, email: regEmail, accept_legal: acceptLegal })
      });
      const data = await res.json();
      if (!res.ok) throw data;
      // BUG FIX: This used to say "pending admin approval" — stale message
      // left over from before self-service registrations were changed to
      // auto-approve. The account is genuinely ready to use immediately, so
      // log straight in rather than bouncing back to a login form asking
      // for credentials just typed in.
      const BASE = API_URL;
      const loginRes = await fetch(`${BASE}/api/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: regEmail, password: regPassword }),
      }).then(r => r.json());
      if (loginRes.token) {
        localStorage.setItem('token', loginRes.token);
        localStorage.setItem('user', JSON.stringify(loginRes.user));
        await login(regEmail, regPassword);

        // PRICING-001: Capture the plan choice made at signup instead of
        // starting everyone on Free and hoping they upgrade later. Reuses
        // the exact same checkout endpoint already used by Pricing.js and
        // the in-app UpgradeModal — no new billing logic, just triggered a
        // step earlier, right when intent to pay is highest.
        if (regPlan === 'agency') {
          navigate('/contact');
          return;
        }
        let checkoutFailed = false;
        let checkoutFailReason = '';
        // BUG FIX: this previously only set a boolean flag — the real
        // reason a checkout failed (a bad/missing price ID, a Stripe-side
        // rejection, an auth issue) was discarded entirely in favour of
        // one generic message, regardless of what actually went wrong.
        // That's exactly what made a real registration issue take a full
        // investigation to diagnose instead of being visible immediately.
        // Captures the backend's own error text now, logs it to the
        // console unconditionally (so it's visible in DevTools even if a
        // user doesn't read the on-screen message), and folds it into
        // what's shown on screen too.
        //
        // PRICE-005: Solo added to the checkout trigger. This condition
        // previously read `regPlan === 'standard' || regPlan === 'pro'` —
        // before Solo existed that correctly caught every paid plan, but
        // it would have silently missed Solo once added: anyone selecting
        // it would skip checkout entirely and land on Free with no
        // indication anything had gone differently than they chose.
        if (regPlan === 'solo' || regPlan === 'standard' || regPlan === 'pro') {
          try {
            const checkoutRes = await fetch(`${BASE}/api/billing/create-checkout-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${loginRes.token}` },
              body: JSON.stringify({ planName: regPlan, interval: regInterval }),
            });
            const checkoutData = await checkoutRes.json();
            if (checkoutRes.ok && checkoutData.url) {
              window.location.href = checkoutData.url;
              return;
            }
            checkoutFailed = true;
            checkoutFailReason = checkoutData.error || `Unexpected response (status ${checkoutRes.status})`;
          } catch (checkoutErr) {
            checkoutFailed = true;
            checkoutFailReason = checkoutErr.message || 'Network error reaching checkout';
          }
        }
        if (checkoutFailed) {
          // Account genuinely exists and works — don't navigate away
          // silently, since the checkout error would never be seen. Show it
          // here and let them continue manually once they've read it.
          console.error('Checkout failed during registration:', checkoutFailReason);
          setCheckoutError(`Could not start checkout (${checkoutFailReason}), but your account was created successfully on the Free plan. You can upgrade anytime from Settings.`);
          setLoading(false);
          return;
        }
        navigate('/');
        return;
      }
      // Fallback if auto-login somehow fails — account still exists and works
      setSuccess('Account created! Please log in to get started.');
      setView('login');
      setRegOrg(''); setRegEmail(''); setRegPassword(''); setRegConfirm(''); setAcceptLegal(false);
    } catch (err) {
      setError(err.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: view === 'register' ? '820px' : '420px' }}>
      <div className="login-card" style={{ width: '100%' }}>
        <div className="login-logo">
          <img src="/casavello-stacked.png" alt="casavello" style={{ width: '140px', height: 'auto', objectFit: 'contain', marginBottom: '0.5rem' }} />
          <h1 style={{ display: 'none' }}>casavello</h1>
          <h1>casavello</h1>
          <p>Your entire property portfolio, effortlessly managed</p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {view === 'mfa' ? (
          <form onSubmit={handleMfaSubmit}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔐</div>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.25rem', fontWeight: 700 }}>Two-Factor Authentication</h2>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                Enter the 6-digit code from your authenticator app
              </p>
            </div>
            {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}
            <div className="form-group" style={{ marginBottom: '1.5rem' }}>
              <input
                type="text"
                inputMode="numeric"
                value={mfaCode}
                onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                style={{ letterSpacing: '0.4em', fontSize: '1.5rem', textAlign: 'center', fontFamily: 'monospace' }}
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', display: 'block', marginTop: '0.5rem', textAlign: 'center' }}>
                Or enter a backup code if you don't have access to your app
              </small>
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%', marginBottom: '0.75rem' }} disabled={mfaLoading || mfaCode.length < 6}>
              {mfaLoading ? 'Verifying...' : '✓ Verify'}
            </button>
            <button type="button" className="btn btn-secondary" style={{ width: '100%', fontSize: '0.875rem' }}
              onClick={() => { setView('login'); setMfaCode(''); setError(''); setMfaPendingToken(''); }}>
              ← Back to login
            </button>
          </form>
        ) : view === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                autoComplete="username"
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>

            {/* Forgot password link */}
            <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '1rem' }}>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', padding: 0 }}
              >
                Forgot password?
              </button>
              {/* BUG-012: for someone who cannot get in because they never verified their email */}
              <div style={{ marginTop: '0.35rem' }}>
                <button
                  type="button"
                  onClick={() => navigate('/resend-verification')}
                  style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline', padding: 0 }}
                >
                  Resend verification email
                </button>
              </div>
            </div>

            {orgOptions.length > 0 && (
              <div className="form-group" style={{marginBottom:'1rem'}}>
                <label style={{fontWeight:600}}>Select your organisation</label>
                <select
                  value={selectedOrg}
                  onChange={e => setSelectedOrg(e.target.value)}
                  style={{width:'100%',padding:'0.5rem',border:'1px solid var(--border)',borderRadius:'0.375rem'}}
                >
                  <option value="">-- Select organisation --</option>
                  {orgOptions.map(org => <option key={org} value={org}>{org}</option>)}
                </select>
                <small style={{color:'var(--text-secondary)'}}>This username exists in multiple organisations. Please select yours.</small>
              </div>
            )}

            <button
              type="submit"
              className="btn btn-success"
              style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginTop: '0.5rem' }}
              disabled={loading || (orgOptions.length > 0 && !selectedOrg)}
            >
              {loading ? 'Logging in...' : '🔐 Login'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => { setView('register'); setError(''); setSuccess(''); setCheckoutError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--brand-green)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem' }}
              >
                Start for free by registering here
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div style={{ background: '#eff6ff', border: '2px solid #3b82f6', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, color: '#1e40af', fontSize: '1rem' }}>✨ Create New Account</h3>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.6rem', color: '#111' }}>Choose your plan</label>
              <div style={{ display: 'flex', gap: '0.25rem', background: '#f3f4f6', borderRadius: '0.5rem', padding: '0.25rem', width: 'fit-content', marginBottom: '1rem' }}>
                {[['month','Monthly'],['year','Annual — 2 months free 🎉']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setRegInterval(key)} style={{
                    padding: '0.375rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: '0.8125rem',
                    background: regInterval === key ? '#fff' : 'transparent',
                    color: regInterval === key ? '#111' : '#6b7280',
                    boxShadow: regInterval === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}>{label}</button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
                {REG_PLANS.map(plan => {
                  const isSelected = regPlan === plan.key;
                  const price = plan.monthlyPrice === null ? 'Custom'
                    : plan.monthlyPrice === 0 ? 'Free'
                    : regInterval === 'year' ? `£${(plan.annualPrice/12).toFixed(2)}/mo` : `£${plan.monthlyPrice.toFixed(2)}/mo`;
                  return (
                    <div
                      key={plan.key}
                      onClick={() => setRegPlan(plan.key)}
                      style={{
                        border: `2px solid ${isSelected ? plan.color : '#e5e7eb'}`,
                        background: isSelected ? `${plan.color}0d` : '#fff',
                        borderRadius: '0.5rem', padding: '0.85rem', cursor: 'pointer', position: 'relative',
                        transition: 'all 0.15s',
                      }}
                    >
                      {plan.popular && (
                        <div style={{ position: 'absolute', top: '-0.55rem', left: '50%', transform: 'translateX(-50%)', background: plan.color, color: '#fff', borderRadius: '1rem', padding: '0.1rem 0.6rem', fontSize: '0.65rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          Most Popular
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                        <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: `2px solid ${isSelected ? plan.color : '#d1d5db'}`, background: isSelected ? plan.color : '#fff', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {isSelected && <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: plan.color }}>{plan.name}</div>
                      </div>
                      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#111' }}>{price}</div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.3rem' }}>{plan.units}</div>
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{plan.tagline}</div>
                    </div>
                  );
                })}
              </div>
              {regPlan !== 'free' && regPlan !== 'agency' && (
                <p style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.6rem', marginBottom: 0, fontWeight: 600 }}>
                  ✓ 14-day free trial, no card charged until your trial ends · Cancel anytime
                </p>
              )}
              {regPlan === 'agency' && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.6rem', marginBottom: 0 }}>
                  You'll be taken to our contact form to discuss your requirements after creating your account.
                </p>
              )}
              {checkoutError && (
                <div style={{ marginTop: '0.6rem' }}>
                  <p style={{ fontSize: '0.8rem', color: '#dc2626', marginBottom: '0.4rem' }}>{checkoutError}</p>
                  <button type="button" onClick={() => navigate('/')} style={{ background: '#166534', color: '#fff', border: 'none', borderRadius: '0.375rem', padding: '0.4rem 0.9rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    Continue to Dashboard →
                  </button>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Organization Name *</label>
              <input
                type="text"
                value={regOrg}
                onChange={e => setRegOrg(e.target.value)}
                placeholder="Your company or organization name"
                required
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                This will be your organization's name in the system
              </small>
            </div>
            {/* BUG FIX: this used to be two separate fields — a free-text
                Username (no format requirement at all) and a separate Email
                Address. That let someone pick a username completely
                unrelated to their email, which is exactly what led to the
                "which organisation did you mean?" login prompt feeling like
                a privacy leak — two unrelated people could coincidentally
                pick the same word. Email is now the ONLY identifier
                collected at registration, used as both the login username
                and the actual email address — a real email is inherently
                tied to one real person, so a collision here only ever means
                the same person legitimately owns more than one organisation,
                never a coincidental stranger clash. Forward-only, same as
                every other change in this area: existing non-email
                usernames (admin, Admin.HibTech etc.) are untouched and keep
                logging in exactly as before. */}
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={regEmail}
                onChange={e => setRegEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoComplete="email"
              />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                This is also how you'll log in — used to verify your account, reset your password and send notifications
              </small>
            </div>
            <div className="form-group">
              <label>Password *</label>
              <input
                type="password"
                value={regPassword}
                onChange={e => setRegPassword(e.target.value)}
                placeholder="Choose a strong password"
                required
              />
              <PasswordRules password={regPassword} />
            </div>
            <div className="form-group">
              <label>Confirm Password *</label>
              <input
                type="password"
                value={regConfirm}
                onChange={e => setRegConfirm(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '1rem', cursor: 'pointer', fontSize: '0.8rem', lineHeight: 1.5 }}>
              <input
                type="checkbox"
                checked={acceptLegal}
                onChange={e => setAcceptLegal(e.target.checked)}
                style={{ marginTop: '0.15rem', flexShrink: 0 }}
              />
              <span>
                I have read and agree to the{' '}
                <a href="/terms" onClick={e => { e.preventDefault(); window.open('/terms', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', cursor: 'pointer' }}>Terms of Service</a>,{' '}
                <a href="/eula" onClick={e => { e.preventDefault(); window.open('/eula', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', cursor: 'pointer' }}>EULA</a>, and{' '}
                <a href="/privacy" onClick={e => { e.preventDefault(); window.open('/privacy', '_blank', 'noopener,noreferrer'); }} style={{ color: 'var(--brand-green)', cursor: 'pointer' }}>Privacy Policy</a>.
              </span>
            </label>
            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.875rem', fontSize: '1rem', marginTop: '0.5rem' }}
              disabled={loading || !acceptLegal || validatePassword(regPassword) !== null}
            >
              {loading ? 'Creating account...' : regPlan === 'free' ? '✅ Create Free Account' : regPlan === 'agency' ? '✅ Create Account & Contact Us' : '✅ Create Account & Start Trial'}
            </button>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => { setView('login'); setError(''); setCheckoutError(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--brand-green)', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.875rem' }}
              >
                Already have an account? Login here
              </button>
            </div>
          </form>
        )}
      </div>
      <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
        <a href="/privacy" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', marginRight: '1rem' }}>Privacy Policy</a>
        <a href="/terms" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', marginRight: '1rem' }}>Terms of Service</a>
        <a href="/eula" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline' }}>EULA</a>
      </div>
      </div>
    </div>
  );
}
